import React from "react";
import { palettes } from "@/components/VivoPlots/functions/drawPlotData/utils/palletes";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { Checkbox } from "antd";
export const ColorPaletteOption = ({
  value,
}: {
  value: keyof typeof palettes;
}) => {
  return (
    <div className="flex flex-row items-center w-full justify-between">
      <p>{value}</p>
      <div className="flex flex-row gap-2">
        {palettes[value].fill.map((color: string) => (
          <div
            key={color}
            className="w-[14px] h-[14px] bg-[#D2D4D5] rounded-xs"
            style={{ backgroundColor: color }}
          ></div>
        ))}
      </div>
    </div>
  );
};

export const ModalOptionLabel = ({
  label,
  tooltip = "",
  disabled = false,
}: {
  label: string;
  tooltip?: string;
  disabled?: boolean;
}) => {
  return (
    <div
      className={`flex items-center gap-2 text-[14px] font-semibold ${
        disabled ? "text-[#8C8C8C]" : "text-[#2B4752]"
      }`}
    >
      <span>{label}</span>
      {tooltip && <InfoTooltip title={tooltip} fill="#00000073" />}
    </div>
  );
};

export const MinValueCheckbox = ({
  zeroAsMinValue,
  setZeroAsMinValue,
}: {
  zeroAsMinValue: boolean;
  setZeroAsMinValue: (updater: (prev: boolean) => boolean) => void;
}) => {
  return (
    <div className=" flex items-center gap-2">
      <Checkbox
        checked={zeroAsMinValue}
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
        onChange={(e) => setZeroAsMinValue((prev: boolean) => !prev)}
      />
      <label className="text-[14px] text-[#2B4752]">Zero-based axis</label>
    </div>
  );
};

export const SortButtons = ({
  xSort,
  setXSort,
}: {
  xSort: "ascending" | "descending" | "none";
  setXSort: (sort: "ascending" | "descending" | "none") => void;
}) => {
  return (
    <div className="flex flex-row gap-1 w-full overflow-hidden">
      <SortButton
        buttonValue="none"
        xSort={xSort}
        onClick={() => setXSort("none")}
      >
        No Sort
      </SortButton>
      <SortButton
        buttonValue="ascending"
        xSort={xSort}
        onClick={() => setXSort("ascending")}
      >
        Ascending
      </SortButton>
      <SortButton
        buttonValue="descending"
        xSort={xSort}
        onClick={() => setXSort("descending")}
      >
        Descending
      </SortButton>
    </div>
  );
};

const SortButton = ({
  children,
  buttonValue,
  xSort,
  onClick,
}: {
  children: React.ReactNode;
  buttonValue: "ascending" | "descending" | "none";
  xSort: "ascending" | "descending" | "none";
  onClick: () => void;
}) => {
  const isSelected = xSort === buttonValue;
  return (
    <button
      aria-pressed={isSelected}
      onClick={onClick}
      className={`text-[14px] font-workSans text-[#2B4752] px-3 py-1 border border-[#E0E0E0] rounded-[4px] ${
        isSelected ? "bg-[#ECFAFF] text-black" : ""
      }`}
    >
      {children}
    </button>
  );
};

export const SortByButtons = ({
  xSortBy,
  setXSortBy,
}: {
  xSortBy: "x" | "y";
  setXSortBy: (sortBy: "x" | "y") => void;
}) => {
  // "X values"/"Y values" follow the visual axis. Under the v2 sort semantics
  // (barplotSortSemanticsVersion), xSortBy "x"/"y" already resolves against the
  // rendered axis position, so the same wiring is correct in both orientations:
  // "x" = whatever sits on the X (bottom) axis, "y" = the Y (left) axis.
  return (
    <div className="flex flex-row gap-1 w-full overflow-hidden text-[14px] font-workSans text-[#8C8C8C]">
      Sorting by:
      <SortByButton value="x" sortBy={xSortBy} onClick={() => setXSortBy("x")}>
        X values
      </SortByButton>
      |
      <SortByButton value="y" sortBy={xSortBy} onClick={() => setXSortBy("y")}>
        Y values
      </SortByButton>
    </div>
  );
};

const SortByButton = ({
  children,
  value,
  onClick,
  sortBy,
}: {
  children: React.ReactNode;
  value: "x" | "y";
  sortBy: "x" | "y";
  onClick: () => void;
}) => {
  const isSelected = sortBy === value;
  return (
    <button
      aria-pressed={isSelected}
      className={`text-[14px] font-workSans focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#06709B] ${
        isSelected ? "underline text-[#06709B]" : "text-[#8C8C8C]"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};
